import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Beaker, Check, X, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandSeparator } from '@/components/ui/command';
import { useTherapyModalities, TherapyModality } from '@/hooks/useTherapyModalities';
import { cn } from '@/lib/utils';

interface TherapyModalityFilterProps {
  selectedModalities: string[];
  onModalitiesChange: (ids: string[]) => void;
  currentStage: string;
  currentDomain: string;
  currentSubcategory: string;
}

// Map stage category name to stage number
const getStageNumber = (category: string): number | null => {
  const mapping: Record<string, number> = {
    'crisis_intervention': 0,
    'harmonize_mind_body': 1,
    'analysis_investigation': 2,
    'transpersonal_dive': 3,
    'actualization_integration': 4,
  };
  return mapping[category] ?? null;
};

const TherapyModalityFilter: React.FC<TherapyModalityFilterProps> = ({
  selectedModalities,
  onModalitiesChange,
  currentStage,
  currentDomain,
  currentSubcategory,
}) => {
  const { t, i18n } = useTranslation('practitioners');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: therapies } = useTherapyModalities();
  const isZh = i18n.language === 'zh';

  // Determine which therapies match current filters
  const { matching, nonMatching } = useMemo(() => {
    if (!therapies) return { matching: [], nonMatching: [] };

    const stageNum = currentStage !== 'all_types' ? getStageNumber(currentStage) : null;
    const hasAnyFilter = stageNum !== null || 
      (currentDomain && currentDomain !== 'all_domains') ||
      (currentSubcategory && currentSubcategory !== 'all_subcategories');

    if (!hasAnyFilter) {
      // No filters active — all therapies are "matching"
      return { matching: [...therapies].sort((a, b) => a.name_en.localeCompare(b.name_en)), nonMatching: [] };
    }

    const match: TherapyModality[] = [];
    const noMatch: TherapyModality[] = [];

    for (const therapy of therapies) {
      let isMatch = true;

      if (stageNum !== null) {
        const stageKey = `stage_${stageNum}` as keyof TherapyModality;
        if (therapy[stageKey] !== true) isMatch = false;
      }

      if (isMatch && currentDomain && currentDomain !== 'all_domains') {
        const domainKey = currentDomain as keyof TherapyModality;
        if (therapy[domainKey] !== true) isMatch = false;
      }

      if (isMatch && currentSubcategory && currentSubcategory !== 'all_subcategories') {
        const subKey = currentSubcategory as keyof TherapyModality;
        if (therapy[subKey] !== true) isMatch = false;
      }

      if (isMatch) {
        match.push(therapy);
      } else {
        noMatch.push(therapy);
      }
    }

    match.sort((a, b) => a.name_en.localeCompare(b.name_en));
    noMatch.sort((a, b) => a.name_en.localeCompare(b.name_en));

    return { matching: match, nonMatching: noMatch };
  }, [therapies, currentStage, currentDomain, currentSubcategory]);

  const toggleModality = useCallback((id: string) => {
    if (selectedModalities.includes(id)) {
      onModalitiesChange(selectedModalities.filter(m => m !== id));
    } else {
      onModalitiesChange([...selectedModalities, id]);
    }
  }, [selectedModalities, onModalitiesChange]);

  const removeModality = useCallback((id: string) => {
    onModalitiesChange(selectedModalities.filter(m => m !== id));
  }, [selectedModalities, onModalitiesChange]);

  const handleDoubleClick = useCallback((therapyId: string) => {
    navigate(`/framework?therapy=${therapyId}`);
  }, [navigate]);

  const getTherapyName = useCallback((therapy: TherapyModality) => {
    const name = isZh ? therapy.name_cn : therapy.name_en;
    return therapy.abbreviation ? `${name} (${therapy.abbreviation})` : name;
  }, [isZh]);

  const getSelectedTherapy = useCallback((id: string) => {
    return therapies?.find(t => t.id === id);
  }, [therapies]);

  const selectedCount = selectedModalities.length;
  const label = isZh ? '疗法' : 'Therapies';
  const matchingLabel = isZh ? '匹配当前筛选' : 'Matching Current Filters';
  const otherLabel = isZh ? '其他疗法' : 'Other Therapies';
  const placeholder = isZh ? '搜索疗法...' : 'Search therapies...';
  const noResults = isZh ? '未找到疗法' : 'No therapies found';
  const selectHint = selectedCount > 0
    ? (isZh ? `已选 ${selectedCount} 个` : `${selectedCount} selected`)
    : (isZh ? '选择疗法...' : 'Select therapies...');
  const doubleClickHint = isZh ? '双击查看详情' : 'Double-click for details';

  return (
    <div>
      <h3 className="text-sm font-semibold mb-2 flex items-center">
        <Beaker className="mr-2 h-4 w-4 text-amber-600" /> {label}
      </h3>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between border-amber-200 text-left font-normal h-auto min-h-[2.5rem] py-2"
          >
            <span className="truncate text-sm">{selectHint}</span>
            <Beaker className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder={placeholder} />
            <CommandEmpty>{noResults}</CommandEmpty>
            
            {/* Matching therapies */}
            {matching.length > 0 && (
              <CommandGroup heading={nonMatching.length > 0 ? matchingLabel : undefined} className="max-h-[200px] overflow-y-auto">
                {matching.map(therapy => (
                  <CommandItem
                    key={therapy.id}
                    value={`${therapy.name_en} ${therapy.name_cn} ${therapy.abbreviation || ''}`}
                    onSelect={() => toggleModality(therapy.id)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 flex-shrink-0",
                        selectedModalities.includes(therapy.id) ? "opacity-100 text-amber-600" : "opacity-0"
                      )}
                    />
                    <span className="truncate text-sm">{getTherapyName(therapy)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Separator */}
            {matching.length > 0 && nonMatching.length > 0 && <CommandSeparator />}

            {/* Non-matching therapies (greyed out) */}
            {nonMatching.length > 0 && (
              <CommandGroup heading={otherLabel} className="max-h-[150px] overflow-y-auto">
                {nonMatching.map(therapy => (
                  <CommandItem
                    key={therapy.id}
                    value={`${therapy.name_en} ${therapy.name_cn} ${therapy.abbreviation || ''}`}
                    onSelect={() => toggleModality(therapy.id)}
                    className="cursor-pointer opacity-50"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 flex-shrink-0",
                        selectedModalities.includes(therapy.id) ? "opacity-100 text-amber-600" : "opacity-0"
                      )}
                    />
                    <span className="truncate text-sm text-muted-foreground">{getTherapyName(therapy)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected badges */}
      {selectedModalities.length > 0 && (
        <div className="mt-2 space-y-1">
          <p className="text-xs text-muted-foreground">{doubleClickHint}</p>
          <div className="flex flex-wrap gap-1.5">
            {selectedModalities.map(id => {
              const therapy = getSelectedTherapy(id);
              if (!therapy) return null;
              const displayName = isZh ? therapy.name_cn : (therapy.abbreviation || therapy.name_en);
              return (
                <Badge
                  key={id}
                  variant="secondary"
                  className="cursor-pointer hover:bg-amber-100 text-xs py-1 px-2 select-none"
                  onDoubleClick={() => handleDoubleClick(id)}
                >
                  {displayName}
                  <X
                    className="ml-1 h-3 w-3 hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeModality(id);
                    }}
                  />
                </Badge>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TherapyModalityFilter;
